import {
  ANIMATABLE_PROPERTIES,
  type AnimKeyframeActionNode,
  type AnimatableProperty,
} from "../../types.js";
import { animKeyframeHasPropertyPort } from "../../anim-keyframe-ports.js";
import {
  BehaviorFlow,
  ExecutionContext,
  NodeExecutionResult,
  PatchResult,
  RuntimeSignal,
} from "../types.js";
import { clamp, normalizeAnimationEasingValue } from "../../method.js";
import { emptyPatchResult, toFiniteNumber } from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

export class AnimKeyframeBehaviorNode extends BehaviorNode {
  declare behavior: AnimKeyframeActionNode;

  constructor(behavior: AnimKeyframeActionNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const patch: PatchResult = emptyPatchResult();
    const visiting = new Set<string>();

    const draft = context.animationDraft;
    if (!draft?.begin) {
      const nextFlows = this.getNextFlows(context);
      const runtimeSignals: RuntimeSignal[] = [];
      if (nextFlows.length === 0 && context.loopStack.length > 0) {
        runtimeSignals.push({ type: "loop_continue", context });
      }
      return {
        patch,
        nextFlows,
        scheduledNodes: [],
        runtimeSignals,
      };
    }

    const offsetRaw = this.getInput("offset", visiting, context);
    const offset = clamp(toFiniteNumber(offsetRaw, 0), 0, 100);

    const hasEasing = this.behavior.ports.some(
      (port) => port.id === "easing" && port.direction === "input",
    );
    const easing = hasEasing
      ? normalizeAnimationEasingValue(
          this.getInput("easing", visiting, context),
        )
      : normalizeAnimationEasingValue(undefined);

    const values: Partial<Record<AnimatableProperty, number>> = {};
    for (const prop of ANIMATABLE_PROPERTIES) {
      if (!animKeyframeHasPropertyPort(this.behavior, prop)) continue;
      const raw = this.getInput(prop, visiting, context);
      values[prop] = toFiniteNumber(raw, 0);
    }

    draft.keyframes.push({ offset, easing, values });

    const nextFlows = this.getNextFlows(context);
    const runtimeSignals: RuntimeSignal[] = [];
    if (nextFlows.length === 0 && context.loopStack.length > 0) {
      runtimeSignals.push({ type: "loop_continue", context });
    }

    return {
      patch,
      nextFlows,
      scheduledNodes: [],
      runtimeSignals,
    };
  }

  getOutput(
    portId: string,
    visiting: Set<string>,
    context: ExecutionContext,
  ): unknown {
    if (portId.endsWith("Out")) {
      const baseId = portId.replace(/Out$/, "");
      return this.getInput(baseId, visiting, context);
    }
    return undefined;
  }

  private getNextFlows(context: ExecutionContext): BehaviorFlow[] {
    return (this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
      edge,
      context,
    }));
  }
}
